/**
 * useBillingLines.js
 * Linhas da fatura de um agendamento (uma por procedimento) e a escolha de origem
 * de cada uma: "package" (desconta uma sessão do pacote) ou "direct" (avulso).
 *
 * Carrega de packages/get_appointment_billing_lines.php. Se o endpoint falhar
 * (rede, 404), cai para linhas feitas do próprio agendamento, todas avulsas e sem
 * `lines` no POST (`payloadLines` = null), para o faturamento antigo seguir valendo.
 *
 * Usado pelo InvoiceForm (agenda v2) e pelo CreateInvoicePopup.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { getBillingLines } from '../../../../../api/AppointmentBillingAPI';

export const SOURCE_PACKAGE = 'package';
export const SOURCE_DIRECT  = 'direct';

const normalizeLine = (line) => ({
  procedure_id:   line.procedure_id != null ? Number(line.procedure_id) : null,
  procedure_name: line.procedure_name || 'Procedimento',
  price:          Number(line.price) || 0,
  duration:       line.duration || null,
  credit:         line.credit || null,
  used:           line.used || null,
});

/** Linhas sintetizadas do agendamento quando o endpoint não responde. */
const fallbackLines = (appointment, GetProcedureById) => {
  const procs = Array.isArray(appointment?.procedures) ? appointment.procedures : [];
  if (procs.length > 0) {
    return procs.map(p => normalizeLine({
      procedure_id: p.procedure_id,
      procedure_name: GetProcedureById(p.procedure_id)?.Label,
      price: p.price,
      duration: p.duration,
      credit: null,
    }));
  }
  if (appointment?.procedure == null) return [];
  return [normalizeLine({
    procedure_id: appointment.procedure,
    procedure_name: GetProcedureById(appointment.procedure)?.Label,
    price: appointment.price,
    credit: null,
  })];
};

/** Escolha inicial: pacote quando há saldo e o plano permite; avulso nos demais. */
const defaultChoices = (lines, locked) => lines.reduce((acc, line) => {
  acc[line.procedure_id] = line.credit && !locked ? SOURCE_PACKAGE : SOURCE_DIRECT;
  return acc;
}, {});

/** Totais da escolha atual: valor a cobrar (avulsos) e sessões de pacote. */
export const summarizeLines = (lines, choices) => {
  let directTotal = 0;
  let packageCount = 0;
  lines.forEach(line => {
    if (choices[line.procedure_id] === SOURCE_PACKAGE) packageCount += 1;
    else directTotal += line.price;
  });
  return {
    directTotal: +directTotal.toFixed(2),
    packageCount,
    directCount: lines.length - packageCount,
    allPackage: lines.length > 0 && packageCount === lines.length,
  };
};

export default function useBillingLines(appointment, { enabled = true, locked = false } = {}) {
  const { GetProcedureById } = DataProviderInstance();
  const [lines,    setLines]    = useState([]);
  const [choices,  setChoices]  = useState({});
  const [walletId, setWalletId] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [fallback, setFallback] = useState(false);

  const appointmentId = appointment?.id;

  const reload = useCallback(() => {
    if (!appointmentId) return;
    setLoading(true);
    getBillingLines(appointmentId, (res) => {
      setLoading(false);
      const list = res?.success && Array.isArray(res.data?.lines) ? res.data.lines : null;
      if (list) {
        const normalized = list.map(normalizeLine);
        setLines(normalized);
        setChoices(defaultChoices(normalized, locked));
        setWalletId(res.data.wallet_id ?? null);
        setFallback(false);
      } else {
        const synthesized = fallbackLines(appointment, GetProcedureById);
        setLines(synthesized);
        setChoices(defaultChoices(synthesized, true));
        setWalletId(null);
        setFallback(true);
      }
    });
  }, [appointmentId, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enabled && appointmentId) reload();
  }, [enabled, appointmentId, reload]);

  const setChoice = useCallback((procedureId, source) => {
    setChoices(prev => ({ ...prev, [procedureId]: source }));
  }, []);

  const summary = useMemo(() => summarizeLines(lines, choices), [lines, choices]);

  // `lines` do POST: null no fallback (POST antigo), senão uma entrada por procedimento
  const payloadLines = useMemo(() => {
    if (fallback) return null;
    return lines
      .filter(line => line.procedure_id != null)
      .map(line => ({
        procedure_id: line.procedure_id,
        source: choices[line.procedure_id] === SOURCE_PACKAGE ? SOURCE_PACKAGE : SOURCE_DIRECT,
      }));
  }, [lines, choices, fallback]);

  return { lines, choices, setChoice, reload, loading, fallback, walletId, summary, payloadLines };
}
